<template>
  <div
    class="selecao-sportv-container"
    :class="[`position-${posicaoEnquete}`, { 'fullscreen-mode': isFullScreen }]"
    :key="item.id"
  >
    <!-- Title Bar (separate from options) -->
    <div v-if="!hideTitle" class="title-bar">
      <span class="title-text">{{ item.dsc_titulo_enquete }}</span>
    </div>

    <!-- Main Content Area (options only) -->
    <div class="main-content">
      <!-- Enquete Image (left side) -->
      <div v-if="hasEnqueteImage" class="enquete-image-section">
        <img
          :src="item.url_imagem_enquete"
          :alt="item.dsc_titulo_enquete"
          class="enquete-image"
          @error="handleEnqueteImageError"
        />
      </div>

      <!-- Options Grid -->
      <div class="options-grid" :class="{ 'has-images': hasOptionsWithImages, 'has-enquete-image': hasEnqueteImage, 'align-start': hasEnqueteImage && hasMultipleOptions }">
        <div
          v-for="opcao in limitedOpcoes"
          :key="opcao.id"
          class="option-item"
        >
          <!-- Image (if available and not broken) - only when NO enquete image -->
          <div v-if="!hasEnqueteImage && opcao.url_imagem && !brokenImages[opcao.id]" class="option-image">
            <img
              :src="opcao.url_imagem"
              :alt="opcao.dsc_titulo"
              @error="handleImageError(opcao.id)"
            />
          </div>

          <div class="option-content">
            <span v-if="!hideOptionTitle" class="option-label">{{ opcao.dsc_titulo }}</span>
            <div v-if="item.flg_exibir_resultado" class="percentage-row">
              <span class="percentage-value">
                {{ animatedPercentages[opcao.id] || 0 }}%
              </span>
              <div class="progress-bar-container">
                <div class="progress-bar-fill" :style="getProgressBarStyle(opcao)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardTarjaEnqueteSportTVNews',
  props: {
    item: Object,
    posicaoEnquete: String,
    isFullScreen: Boolean,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
  },
  data() {
    return {
      animatedPercentages: {},
      animationDuration: 1200,
      optionColors: {},
      brokenImages: {},
      brokenEnqueteImage: false,
      animationFrameIds: {},
    };
  },
  created() {
    for (const option of (this.item?.enquete_opcoes || []).slice(0, 8)) {
      if (option.dsc_cor_barra) {
        this.optionColors[option.id] = option.dsc_cor_barra;
      }
    }
  },
  computed: {
    limitedOpcoes() {
      const maxOptions = this.hasEnqueteImage ? 4 : 8;
      return (this.item?.enquete_opcoes || []).slice(0, maxOptions);
    },
    hasOptionsWithImages() {
      return this.limitedOpcoes.some(
        opcao => opcao.url_imagem && !this.brokenImages[opcao.id]
      );
    },
    hasEnqueteImage() {
      return this.item?.url_imagem_enquete && !this.brokenEnqueteImage;
    },
    hasMultipleOptions() {
      return this.limitedOpcoes.length > 3;
    },
  },
  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal) {
          this.limitedOpcoes.forEach((opcao) => {
            this.animateValue(opcao.id, 0, opcao.resultado || 0);
          });
        } else {
          this.animatedPercentages = {};
        }
      },
      immediate: true,
    },
    'item.enquete_opcoes': {
      handler(newOpcoes, oldOpcoes) {
        if (newOpcoes) {
          const limitedNewOpcoes = newOpcoes.slice(0, 8);
          const limitedOldOpcoes = oldOpcoes?.slice(0, 8);

          limitedNewOpcoes.forEach((opcao) => {
            const oldOpcao = limitedOldOpcoes?.find(o => o.id === opcao.id);
            if (oldOpcao && oldOpcao.url_imagem !== opcao.url_imagem) {
              this.$set(this.brokenImages, opcao.id, false);
            }
          });

          if (this.item.flg_exibir_resultado) {
            limitedNewOpcoes.forEach((opcao) => {
              const currentValue = this.animatedPercentages[opcao.id] || 0;
              if (currentValue !== opcao.resultado) {
                this.animateValue(opcao.id, currentValue, opcao.resultado || 0);
              }
              if (opcao.dsc_cor_barra) {
                this.$set(this.optionColors, opcao.id, opcao.dsc_cor_barra);
              } else {
                this.$delete(this.optionColors, opcao.id);
              }
            });
          }
        }
      },
      deep: true,
    },
    'item.url_imagem_enquete': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.brokenEnqueteImage = false;
        }
      },
    },
  },
  beforeDestroy() {
    Object.values(this.animationFrameIds).forEach(id => cancelAnimationFrame(id));
  },
  methods: {
    animateValue(key, fromValue, toValue) {
      if (this.animationFrameIds[key]) {
        cancelAnimationFrame(this.animationFrameIds[key]);
      }
      const startTime = Date.now();
      const difference = toValue - fromValue;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        this.$set(
          this.animatedPercentages,
          key,
          Math.round(fromValue + difference * easeOutCubic)
        );

        if (progress < 1) {
          this.animationFrameIds[key] = requestAnimationFrame(animate);
        } else {
          delete this.animationFrameIds[key];
        }
      };

      this.animationFrameIds[key] = requestAnimationFrame(animate);
    },
    getProgressBarStyle(opcao) {
      const percentage = this.animatedPercentages[opcao.id] || 0;
      const style = { width: percentage + '%' };
      if (this.optionColors[opcao.id]) {
        style.backgroundColor = this.optionColors[opcao.id];
      }
      return style;
    },
    handleImageError(opcaoId) {
      this.$set(this.brokenImages, opcaoId, true);
    },
    handleEnqueteImageError() {
      this.brokenEnqueteImage = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Poppins";
  font-weight: 700;
  src: url("@/assets/fonts/Poppins-Bold.ttf") format("truetype");
}

@font-face {
  font-family: "GaleanaCondensed-BlackIt";
  src: url("@/assets/fonts/GaleanaCondensed-BlackIt.otf") format("opentype");
}

@font-face {
  font-family: "GaleanaCondensed-HeavyIt";
  src: url("@/assets/fonts/GaleanaCondensed-HeavyIt.otf") format("opentype");
}

.selecao-sportv-container {
  --background-color: #E6D200;
  --title-bar-bg: #0A043E;
  --title-text-color: #ffffff;
  --progress-bar-bg: #F1F1F1;
  --progress-bar-fill: #404040;
  --option-text-color: #020202;
  --percentage-text-color: #020202;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: fit-content;
  min-width: 820px;
  height: 178px;
  margin-bottom: 85px;

  &.position-esquerda {
    justify-self: flex-start;
    margin-left: 50px;
  }

  &.position-centro {
    justify-self: center;
    margin-left: auto;
    margin-right: auto;
  }

  &.position-direita {
    justify-self: flex-end;
    margin-right: 50px;
  }

  &.fullscreen-mode {
    max-width: 1200px;

    .main-content {
      transform: skewX(-20deg);
    }

    .title-bar {
      padding: 14px 40px;
      margin-left: 30px;
      margin-bottom: 12px;

      .title-text {
        font-size: 32px;
      }
    }

    .options-grid {
      padding: 30px 40px;
    }

    .percentage-value {
      font-size: 42px;
    }

    .option-label {
      font-size: 28px;
    }

    .option-image img {
      width: 120px;
      height: 120px;
    }

    .progress-bar-container {
      width: 120px;
      height: 28px;
    }

    .enquete-image-section {
      padding: 15px 10px 15px 25px;

      .enquete-image {
        width: 120px;
        height: 120px;
      }
    }
  }
}

.main-content {
  background-color: var(--background-color);
  transform: skewX(-20deg);
  overflow: hidden;
  position: relative;
  flex: 1;
  display: flex;
  min-width: 820px;
  flex-direction: row;
  align-items: center;
}

.enquete-image-section {
  display: flex;
  align-items: center;
  align-self: flex-end;
  justify-content: center;
  padding: 18px 5px 18px 30px;
  flex-shrink: 0;
  transform: skewX(20deg);

  .enquete-image {
    width: 90px;
    height: 90px;
    object-fit: contain;
  }
}

.title-bar {
  background-color: var(--title-bar-bg);
  padding: 8px 30px;
  width: fit-content;
  margin-left: 2px;
  margin-bottom: -25px;
  transform: skewX(-20deg);
  z-index: 1;

  .title-text {
    display: block;
    transform: skewX(20deg);
    color: var(--title-text-color);
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    font-size: 25px;
    text-align: center;
  }
}

.options-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-end;
  gap: 20px;
  padding: 35px 15px 10px 15px;
  transform: skewX(20deg);

  &.has-enquete-image {
    padding-left: 0;
  }

  &.align-start {
    justify-content: flex-start;
  }
}

.option-item {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 15px;
  padding: 5px 10px;

  &:first-child {
    margin-left: 50px;
  }

  &:last-child {
    margin-right: 50px;
  }
}

.percentage-value {
  font-family: 'GaleanaCondensed-HeavyIt', sans-serif;
  font-size: 25px;
  color: var(--percentage-text-color);
  min-width: 40px;
}

.option-image {
  flex-shrink: 0;

  img {
    width: 90px;
    height: 90px;
    object-fit: contain;
  }
}

.option-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.option-label {
  font-family: 'GaleanaCondensed-BlackIt', sans-serif;
  font-size: 25px;
  color: var(--option-text-color);
  white-space: pre-line;
  text-align: left;
  line-height: 1.1;
}

.percentage-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: -2px;
  gap: 8px;
}

.progress-bar-container {
  min-width: 80px;
  width: 80px;
  height: 20px;
  background-color: var(--progress-bar-bg);
  transform: skewX(-20deg);
  overflow: hidden;
  flex-shrink: 0;
}

.progress-bar-fill {
  height: 100%;
  background-color: var(--progress-bar-fill);
  transition: width 0.3s ease;
}
</style>
